import extractErrorMessage from '@lib/utils/extractErrorMessage';
import TasksModule from '@modules/TasksModule';
import useTasks from '@modules/TasksModule/hooks/useTasks';
import React, { useCallback, useMemo, useState } from 'react';
import { SearchParams } from '../components/CustomerSearchBox/CustomerSearchBox';
import searchCustomers from '../data/searchCustomers';
import { CustomerBySearchType } from '../data/types';
import CustomerSearchContext, {
  SelectCustomerType,
} from './CustomerSearchContext';

interface CustomerSearchProviderProps {
  tenantId?: string;
  taskId?: string;
  children: React.ReactNode;
  searchParams: SearchParams;
  setSearchParams: (params: SearchParams) => void;
  onAddCustomer?: () => void;
}

export const CustomerSearchProvider: React.FC<CustomerSearchProviderProps> = ({
  children,
  tenantId,
  taskId,
  searchParams,
  setSearchParams,
  onAddCustomer,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [results, setResults] = useState<CustomerBySearchType[] | null>(null);
  const [failedTenants, setFailedTenants] = useState<string[] | null>(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<
    SelectCustomerType | undefined
  >(undefined);
  const { taskById } = useTasks();
  const task = taskId ? taskById[taskId] : null;

  const onSelectCustomer = useCallback(
    (customer?: Partial<CustomerBySearchType>) => {
      if (!task) {
        return;
      }
      if (!customer) {
        return;
      }
      TasksModule.setTaskAttribute(task.id, 'customerId', customer.id);
      TasksModule.setTaskDisplayName(task.id, customer.name);
    },
    [task],
  );

  const search = useCallback(
    async (params: SearchParams) => {
      try {
        setLoading(true);
        setError('');
        setSelectedCustomer(undefined);
        onSelectCustomer(undefined);
        const results = await searchCustomers(
          {
            name: params.name === '' ? undefined : params.name,
            phone: params.phone === '' ? undefined : params.phone,
            street: params.street === '' ? undefined : params.street,
            unit: params.unit === '' ? undefined : params.unit,
            zip: params.zip === '' ? undefined : params.zip,
          },
          tenantId,
        );
        setResults(results.customers);
        setFailedTenants(results.failedTenants);
        return results;
      } catch (err) {
        setError(extractErrorMessage(err));
      } finally {
        setLoading(false);
        setFirstLoad(false);
      }
    },
    [tenantId, onSelectCustomer],
  );

  const value = useMemo(
    () => ({
      searchParams,
      setSearchParams,
      loading,
      error,
      results,
      failedTenants,
      firstLoad,
      selectedCustomer,
      search,
      onSelectCustomer,
      onAddCustomer,
      setResults,
      setFailedTenants,
    }),
    [
      searchParams,
      setSearchParams,
      loading,
      error,
      results,
      failedTenants,
      firstLoad,
      selectedCustomer,
      search,
      onSelectCustomer,
      onAddCustomer,
      setResults,
      setFailedTenants,
    ],
  );

  return (
    <CustomerSearchContext.Provider value={value}>
      {children}
    </CustomerSearchContext.Provider>
  );
};
